import { Injectable } from '@angular/core';

import { JwtUser } from '@renovationman/ts-utils';

import { environment } from '~env';

/**
 * Quelle application accueille quel utilisateur.
 *
 * Extrait de `LoginPage` pour être partagé avec la route de rappel du portail : les deux ont besoin de
 * la même correspondance, mais pas de la même source. La page de login lit le `JwtUser` du
 * `localStorage` ; le rappel le lit dans le jeton d'identité qu'il vient de recevoir, avant d'avoir
 * quoi que ce soit de rangé. D'où un service qui prend l'utilisateur en paramètre plutôt que d'aller
 * le chercher.
 *
 * <strong>Pourquoi c'est ici que la décision se prend.</strong> En OAuth 2, la `redirect_uri` est
 * fournie par le client et validée contre sa liste blanche : le serveur d'autorisation revient
 * toujours sur celle-là et ne peut pas choisir une destination selon une logique interne. Ce n'est pas
 * une limitation à contourner — c'est la propriété qui ferme la redirection ouverte. L'aiguillage
 * appartient donc au portail, qui est le client, et à lui seul.
 */
@Injectable({ providedIn: 'root' })
export class UserDestinationService {
  /** L'espace par défaut quand le type est inconnu ou l'utilisateur illisible. */
  private readonly fallback = environment.uiBasePaths.client;

  baseUrlFor(jwtUser: JwtUser | null): string {
    if (!jwtUser) {
      return this.fallback;
    }
    if (jwtUser.isClient) {
      return environment.uiBasePaths.client;
    } else if (jwtUser.isArtisan) {
      return environment.uiBasePaths.artisan;
    } else if (jwtUser.isPartner) {
      return environment.uiBasePaths.partner;
    } else if (jwtUser.isEmployee) {
      return environment.uiBasePaths.employe;
    }
    return this.fallback;
  }

  urlFor(jwtUser: JwtUser | null): URL {
    return new URL(this.baseUrlFor(jwtUser));
  }

  /**
   * L'`originUrl` demandée est-elle l'un de nos espaces ?
   *
   * <strong>Pourquoi ce filtre existe.</strong> `originUrl` est un paramètre d'URL, donc fourni par
   * qui veut, et la page de login y redirige <em>avec le jeton en query param</em>. Sans liste
   * blanche, `login.renovationman.com/login?originUrl=https://ailleurs.example` est un lien qui
   * exfiltre le jeton de quiconque le clique et se connecte. Vérifier que l'URL parse ne protège de
   * rien : `https://ailleurs.example` parse parfaitement.
   *
   * La comparaison porte sur l'**origine** et non sur la chaîne entière, parce que le lien profond
   * légitime porte un chemin et une query — c'est tout son intérêt. `new URL()` normalise l'origine
   * (schéma, hôte, port), ce qui ferme les variantes d'écriture d'un même hôte.
   *
   * C'est le pendant « URL absolue » de `safeResumeUri` et de `safeDeepLink`, qui n'acceptent, eux,
   * que des chemins relatifs. Trois validations de redirection, une par forme — et aucune qui en
   * laisse passer une quatrième.
   */
  isKnownSpaceUrl(candidate: URL): boolean {
    return Object.values(environment.uiBasePaths).some(
      (baseUrl) => new URL(baseUrl).origin === candidate.origin
    );
  }
}
